import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  getReviewStatus,
  hasPendingEdits,
  REVIEW_STATUS_LABEL,
} from "@/features/content/lib/content-review-status";
import type { ContentEntry } from "@/types/content";

export function ContentTypeBadge({ isNews }: { isNews?: boolean }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs font-normal",
        isNews
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-border bg-muted/30 text-muted-foreground",
      )}
    >
      {isNews ? "خبر" : "صفحة"}
    </Badge>
  );
}

export function ContentPublishBadge({ entry }: { entry: ContentEntry }) {
  const published = Boolean(entry.published);
  const pending = hasPendingEdits(entry);

  if (published) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <Badge
          variant="outline"
          className="border-primary/30 bg-primary/10 text-xs text-primary"
        >
          منشور
        </Badge>
        {pending && (
          <span className="text-[10px] leading-tight text-amber-700 dark:text-amber-400">
            تعديلات معلقة
          </span>
        )}
      </div>
    );
  }

  return (
    <Badge
      variant="outline"
      className="border-border bg-muted/40 text-xs text-muted-foreground"
    >
      غير منشور
    </Badge>
  );
}

export function ContentReviewBadge({ entry }: { entry: ContentEntry }) {
  const reviewStatus = getReviewStatus(entry);
  const pending = hasPendingEdits(entry);

  if (pending) {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 text-xs text-amber-800 dark:text-amber-300"
      >
        تعديلات غير منشورة
      </Badge>
    );
  }

  const variant =
    reviewStatus === "APPROVED"
      ? "default"
      : reviewStatus === "REJECTED"
        ? "destructive"
        : reviewStatus === "SUBMITTED"
          ? "outline"
          : "secondary";

  const className =
    reviewStatus === "SUBMITTED"
      ? "border-accent/40 bg-accent/10 text-accent"
      : undefined;

  return (
    <Badge variant={variant} className={cn("text-xs", className)}>
      {REVIEW_STATUS_LABEL[reviewStatus]}
    </Badge>
  );
}
